import { createApi } from "@reduxjs/toolkit/query/react";
import { axiosBaseQuery } from "@src/service";
import { IGetDronesRequest, IGetDronesResponse, IGetPaginatedDronesRequest, IGetPaginatedDronesResponse } from "./types";

const DRONES_PAGE_SIZE = 100;

export const baseDroneApi = createApi({
  baseQuery: axiosBaseQuery(),
  reducerPath: 'baseDroneApi',
  tagTypes: ['GetDrones', 'GetDroneHistory', 'GetDroneMaintenance', 'Drone'],
  endpoints: (builder) => ({
    getDrones: builder.query<IGetDronesResponse, IGetDronesRequest | void>({
      queryFn: async (arg, _api, _extraOptions, baseQuery) => {
        const statusParam = arg && arg.status ? { status: arg.status } : {};

        const firstPage = await baseQuery({
          url: '/api/v1/drone',
          method: 'GET',
          params: { page: 0, size: DRONES_PAGE_SIZE, ...statusParam },
        });
        if (firstPage.error) return { error: firstPage.error };

        const firstData = firstPage.data as IGetDronesResponse;
        const totalCount = firstData.totalCount ?? firstData.data.length;
        const pageSize = firstData.data.length || DRONES_PAGE_SIZE;
        const totalPages = pageSize > 0 ? Math.ceil(totalCount / pageSize) : 1;

        if (totalPages <= 1) return { data: firstData };

        const restRequests = Array.from({ length: totalPages - 1 }, (_, i) =>
          baseQuery({
            url: '/api/v1/drone',
            method: 'GET',
            params: { page: i + 1, size: pageSize, ...statusParam },
          })
        );
        const restResults = await Promise.all(restRequests);

        const failed = restResults.find((r) => r.error);
        if (failed?.error) return { error: failed.error };

        const merged = restResults.reduce<typeof firstData.data>(
          (acc, r) => acc.concat((r.data as IGetDronesResponse).data),
          firstData.data
        );

        return {
          data: {
            data: merged,
            totalCount,
            timestamp: firstData.timestamp,
          },
        };
      },
      providesTags: ['GetDrones'],
    }),
    getPaginatedDrones: builder.query<IGetPaginatedDronesResponse, IGetPaginatedDronesRequest>({
      query: ({ page, size, sort }) => ({
        url: '/api/v1/drone',
        method: 'GET',
        params: {
          page,
          size,
          sort,
        },
      }),
      providesTags: ['GetDrones'],
    }),
  })
});

export const { useGetDronesQuery, useGetPaginatedDronesQuery } = baseDroneApi;